import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../Avatar';
import type { TimelineEvent } from '../actions/types';
import { getEventVisualConfig } from './event-config';
import { useStatusCatalog } from '../../../services/statusCatalog';
import { resolveActorDisplay } from './event-actor';
import { formatRelative, formatShortDate } from './event-time';

interface TimelineRailEventProps {
    event: TimelineEvent;
    isLast: boolean;
}

const ACTOR_AVATAR_SIZE_PX = 16;

function TimelineRailEvent({ event, isLast }: TimelineRailEventProps) {
    const config = getEventVisualConfig(event.type);
    const Icon = config.icon;
    const tone = config.tone;
    const isInvalidated = event.status === 'invalidated';
    const title = config.formatTitle(event);
    const catalog = useStatusCatalog();
    const body = config.formatBody?.(event, catalog) ?? null;
    const actor = resolveActorDisplay(event);
    const relative = formatRelative(event.createdAt);
    const shortDate = formatShortDate(event.createdAt);

    // Cards with body content are click-to-expand. Cards without one render
    // as static rows (no chevron, no toggle).
    const expandable = body !== null;
    const [expanded, setExpanded] = useState(false);

    return (
        <li className={`relative pl-7 pb-2.5 ${isInvalidated ? 'opacity-60' : ''}`}>
            {!isLast && <span className="absolute left-[10px] top-5 bottom-0 w-px bg-gray-200" />}
            <span
                className={`absolute left-[2px] top-1.5 w-[18px] h-[18px] rounded-full border ${tone.ring} flex items-center justify-center ${tone.icon}`}
            >
                <Icon className="w-3 h-3" />
            </span>
            <div
                className={`rounded-lg border border-gray-200 bg-white px-2.5 py-2 font-inter transition-colors ${
                    expandable ? 'hover:border-gray-300' : ''
                }`}
            >
                {/* Only the header toggles — the expanded body below stays a plain,
            selectable region so its details can be copied without a click
            collapsing the card. */}
                <div
                    className={`flex items-baseline justify-between gap-2 ${
                        expandable ? 'cursor-pointer select-none' : ''
                    }`}
                    onClick={expandable ? () => setExpanded((v) => !v) : undefined}
                    onKeyDown={
                        expandable
                            ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setExpanded((v) => !v);
                                  }
                              }
                            : undefined
                    }
                    role={expandable ? 'button' : undefined}
                    tabIndex={expandable ? 0 : undefined}
                    aria-expanded={expandable ? expanded : undefined}
                >
                    <span
                        className={`text-xs font-semibold text-gray-900 leading-tight truncate ${
                            isInvalidated ? 'line-through' : ''
                        }`}
                    >
                        {title}
                    </span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {relative && <span className="text-[10px] text-gray-400">{relative}</span>}
                        {expandable && (
                            <ChevronDownIcon
                                className={`w-3 h-3 text-gray-400 transition-transform duration-150 ${
                                    expanded ? 'rotate-180' : ''
                                }`}
                            />
                        )}
                    </div>
                </div>

                {isInvalidated && event.invalidationReason && (
                    <div className="text-xs text-red-500 leading-snug mt-1">
                        Invalidated · {event.invalidationReason}
                    </div>
                )}

                {expandable && expanded && (
                    <div className="mt-2 pt-2 border-t border-gray-100">{body}</div>
                )}

                <div className="flex items-center gap-1.5 mt-1.5">
                    <div
                        className="flex-shrink-0"
                        style={{ width: ACTOR_AVATAR_SIZE_PX, height: ACTOR_AVATAR_SIZE_PX }}
                    >
                        <Avatar
                            user={{ name: actor.name, email: actor.email }}
                            imageURL={actor.imageURL}
                        />
                    </div>
                    <span className="text-[11px] text-gray-500 truncate flex-1 capitalize">
                        {actor.name}
                    </span>
                    {shortDate && <span className="text-[10px] text-gray-300">{shortDate}</span>}
                </div>
            </div>
        </li>
    );
}

export { TimelineRailEvent };
